import { useEffect, useRef } from "react";
import { ORDER_REFRESH_INTERVAL_MS, shouldRefreshOrders } from "./orderRefresh";

interface UseOrderRefreshOptions {
  /** 這個畫面要不要跑背景更新 */
  active: boolean;
  /** 查詢的前置條件是否就緒（例如 cusJwtToken） */
  ready: boolean;
  /** 畫面上還有會變的訂單嗎；全是終局狀態就不必再查 */
  hasLive: boolean;
  /** 已有查詢在進行中（含手動觸發） */
  externalInFlight?: boolean;
  /** 靜默查一次：不切換任何可見的 loading，失敗也不跳提示 */
  run: () => Promise<void> | void;
}

/**
 * 訂單資料的背景更新。
 *
 * 和 usePendingOrderPolling 是姊妹：那支專門等「訂單被建立」，所以間隔短（5 秒）
 * 且有 10 分鐘的工作階段上限；這支是讓已經在畫面上的訂單跟上狀態變化，
 * 所以間隔長（30 秒）、沒有時限，但會在訂單都進入終局狀態時自己停下來。
 *
 * 分頁切回前景時立即補查一次——在 LINE 裡切出去再切回來是常態，那一刻正是
 * 使用者最可能發現「咦狀態怎麼還沒變」的時候。
 */
export const useOrderRefresh = ({
  active,
  ready,
  hasLive,
  externalInFlight = false,
  run,
}: UseOrderRefreshOptions): void => {
  // 用 ref 保存最新值，避免它們變動導致 effect 重跑、interval 被重建
  const runRef = useRef(run);
  runRef.current = run;
  const externalInFlightRef = useRef(externalInFlight);
  externalInFlightRef.current = externalInFlight;
  const hasLiveRef = useRef(hasLive);
  hasLiveRef.current = hasLive;
  const internalInFlightRef = useRef(false);

  useEffect(() => {
    if (!active || !ready) return;

    const tick = () => {
      const ok = shouldRefreshOrders({
        visible: document.visibilityState === "visible",
        inFlight: internalInFlightRef.current || externalInFlightRef.current,
        hasLive: hasLiveRef.current,
      });
      if (!ok) return;
      internalInFlightRef.current = true;
      Promise.resolve()
        .then(() => runRef.current())
        .catch(() => {
          // 背景更新失敗不提示：畫面維持現狀，下一拍再試
        })
        .finally(() => {
          internalInFlightRef.current = false;
        });
    };

    const intervalId = window.setInterval(tick, ORDER_REFRESH_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [active, ready]);
};
